/**
 * WES Identity — cliente das rotas `/identity`.
 *
 * Os contratos são os de `backend/app/schemas/identity.py`; este arquivo só
 * os traduz para chamadas. Regra de negócio (quando mascarar, quanto tempo
 * até expirar, quantas tentativas de PIN) mora no servidor: a tela renderiza
 * o que ele diz e não crava número nenhum.
 */

import apiClient from './apiClient';

// Estados de `GET /identity/session/state` (identity_session_service.py).
export const SESSION_NONE = 'none';
export const SESSION_ACTIVE = 'active';
export const SESSION_MASKED = 'masked';
export const SESSION_ENDED = 'ended';

// Motivos de encerramento (models/identity_session.py).
export const END_REASON_TIMEOUT = 'timeout';
export const END_REASON_HANDOVER = 'handover';

/** Lê o estado sem contar como atividade. */
export async function getSessionState() {
  const { data } = await apiClient.get('/identity/session/state');
  return data;
}

/** Atividade real do médico. Sessão mascarada não volta por heartbeat. */
export async function sendHeartbeat() {
  const { data } = await apiClient.post('/identity/session/heartbeat');
  return data;
}

/** Sai do mascaramento pelo PIN. */
export async function reverifyWithPin(pin) {
  const { data } = await apiClient.post('/identity/session/reverify', { pin });
  return data;
}

/**
 * Sai do mascaramento pelo rosto. A pergunta aqui é "é a mesma pessoa?", e
 * não "de quem é este rosto?" — por isso não é o `/login/face`, que abriria
 * uma sessão nova para quem passasse na frente da câmera.
 */
export async function reverifyWithFace(blob) {
  const form = new FormData();
  form.append('image', blob, 'rosto.jpg');
  const { data } = await apiClient.post('/identity/session/reverify/face', form);
  return data;
}

/**
 * Motivo de uma recusa na reconfirmação pelo rosto.
 *
 * Os códigos são os do `/login/face`, mais `different_person` (rosto
 * conhecido, mas não é o dono da sessão). Um 409 sem código é a sessão que
 * acabou enquanto a máscara estava de pé.
 */
export function reverifyFaceFailureCode(error) {
  const response = error && error.response;
  if (!response) return 'recognizer_unavailable';
  const detail = response.data && response.data.detail;
  if (detail && typeof detail === 'object' && detail.code) return detail.code;
  switch (response.status) {
    case 404:
      return 'not_recognized';
    case 413:
    case 422:
      return 'capture_failed';
    case 403:
      return 'different_person';
    case 409:
      return 'session_gone';
    default:
      return 'recognizer_unavailable';
  }
}

// Versão do aviso de finalidade que o médico aceita no primeiro acesso.
// Combinada com o backend (C3); muda quando o texto do aviso mudar.
export const CONSENT_TEXT_VERSION = 'v1-2026-09';

/**
 * Primeiro acesso: aceite, captura e PIN numa chamada só.
 *
 * Multipart porque a captura vai junto. O backend a valida e registra na
 * auditoria, e descarta os bytes; ela ainda não chega ao Face ID da Neuro.
 * A captura é opcional no contrato: sem ela, a etapa simplesmente não houve.
 */
export async function enrollIdentity({ pin, faceBlob }) {
  const form = new FormData();
  form.append('consent_accepted', 'true');
  form.append('consent_text_version', CONSENT_TEXT_VERSION);
  form.append('pin', pin);
  if (faceBlob) form.append('face_image', faceBlob, 'rosto.jpg');
  const { data } = await apiClient.post('/identity/enroll', form);
  return data;
}

/**
 * A mesma regra do `validate_pin` do backend, para a tela avisar enquanto a
 * pessoa digita em vez de só depois de enviar. O backend continua sendo quem
 * decide; isto é só antecipar a resposta dele.
 *
 * Devolve o motivo da recusa, ou `null` quando o PIN serve.
 */
export function pinRuleViolation(pin) {
  if (!/^\d{4,8}$/.test(pin || '')) return 'length';
  if (new Set(pin).size === 1) return 'repeated';
  const digits = pin.split('').map(Number);
  const deltas = new Set(digits.slice(1).map((d, i) => d - digits[i]));
  if (deltas.size === 1 && (deltas.has(1) || deltas.has(-1))) return 'sequence';
  return null;
}

// ---- administração (P2) -----------------------------------------------------

// Tipos de estação (models/workstation.py).
export const STATION_SHARED = 'shared';
export const STATION_PERSONAL = 'personal';

/** Estações do cliente. Só para administrador. */
export async function listStations() {
  const { data } = await apiClient.get('/identity/stations');
  return data;
}

/**
 * Cadastra uma estação. A resposta traz `station_key` — a ÚNICA vez em que
 * ela aparece; quem cadastra precisa levá-la até a máquina.
 */
export async function createStation({ unit, room, stationType }) {
  const { data } = await apiClient.post('/identity/stations', {
    unit,
    room,
    station_type: stationType,
  });
  return data;
}

/** Altera o cadastro. Mudança de tipo vale a partir do próximo login. */
export async function updateStation(stationId, { unit, room, stationType, isActive }) {
  const { data } = await apiClient.patch(`/identity/stations/${stationId}`, {
    unit,
    room,
    station_type: stationType,
    is_active: isActive,
  });
  return data;
}

/** Link que configura a máquina como estação: abre o login com a chave. */
export function stationSetupUrl(stationKey) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/login?station_key=${encodeURIComponent(stationKey)}`;
}

// Ações do WES Identity em audit_logs. O `/audit/` filtra por trecho do nome
// da ação, e as três começam com `identity_`.
export const HISTORY_ACTION_FILTER = 'identity_';
export const ACTION_LOGIN = 'identity_login';
export const ACTION_PIN_LOGIN = 'identity_pin_login';
export const ACTION_ENROLL = 'identity_enroll';

/**
 * Histórico de acessos, pelo `/audit/` genérico: a POC não tem rota própria
 * (`GET /identity/audit` ficou no mapa, sem implementação). O backend filtra
 * período e médico (a busca cobre nome e e-mail); estação está nos detalhes
 * do registro e é filtrada na tela.
 */
export async function listAccessHistory({ startDate, search, limit = 100 } = {}) {
  const params = { action: HISTORY_ACTION_FILTER, limit };
  if (startDate) params.start_date = `${startDate}T00:00:00`;
  if (search) params.search = search;
  const { data } = await apiClient.get('/audit/', { params });
  return data;
}

/**
 * Demonstração: quais médicos o reconhecimento encenado conhece, qual está
 * valendo e o cenário forçado. 404 quando o ambiente não tem a demonstração.
 */
export async function getDemoState() {
  const { data } = await apiClient.get('/identity/demo/state');
  return data;
}

/**
 * Troca o médico reconhecido, o cenário, ou os dois. O estado vale para o
 * ambiente inteiro, não só para esta tela: é o que o reconhecedor usa.
 */
export async function selectDemoFace({ label, scenario }) {
  const { data } = await apiClient.post('/identity/demo/select-face', { label, scenario });
  return data;
}

/** "Sou outra pessoa": encerra a sessão e apaga os cookies. Não abre outra. */
export async function handoverSession() {
  const { data } = await apiClient.post('/identity/session/handover');
  return data;
}

/**
 * Entrada pelo rosto. Um passo só: o quadro vai, a sessão volta — os tokens
 * nos cookies, o corpo com nome, estação e modo restrito.
 */
export async function loginWithFace(blob) {
  const form = new FormData();
  form.append('image', blob, 'rosto.jpg');
  const { data } = await apiClient.post('/identity/login/face', form);
  return data;
}

/**
 * Entrada pelo PIN. O e-mail vai junto porque a verificação que falhou não
 * identificou ninguém — o PIN sozinho não diz de quem é.
 */
export async function loginWithPin(email, pin, reason = null) {
  const body = { email, pin };
  // O motivo do PIN (protótipo: "fica registrado no histórico"). O backend
  // ainda não tem o campo e o ignora; vai no corpo para o registro começar a
  // valer assim que o `PinLoginRequest` o aceitar, sem mudar a tela.
  if (reason) {
    body.reason = reason.code;
    if (reason.detail) body.reason_detail = reason.detail;
  }
  const { data } = await apiClient.post('/identity/login/pin', body);
  return data;
}

export const IDENTITY_ON = 'on';
export const IDENTITY_OFF = 'off';
export const IDENTITY_UNKNOWN = 'unknown';

/**
 * A POC está acesa para o tenant de quem acabou de entrar por senha?
 *
 * O estado da sessão responde 200 (`none`) onde a POC existe e 404 onde não.
 * É o que separa o login em duas etapas (senha, depois rosto) do login de
 * sempre: sem esta pergunta, todo tenant do Core cairia no pop-up do rosto.
 *
 * Devolve três estados, e não um booleano, porque a versão booleana tratava
 * **qualquer** erro como "desligada" (Bug #5145). Numa estação isso fazia o
 * login seguir só com a senha — exatamente o que a segunda etapa existe para
 * impedir —, e acontecia de verdade: durante todo rollout os pods respondem
 * 502/503 por alguns segundos, e o próprio apply provoca um rollout.
 *
 * Só o 404 é "desligada": é o colapso que `require_wes_identity_tenant` faz
 * quando o tenant não tem a POC. Todo o resto é indisponibilidade, e quem
 * chama precisa poder falhar fechado.
 */
export async function identityStateForCurrentUser() {
  try {
    await apiClient.get('/identity/session/state');
    return IDENTITY_ON;
  } catch (error) {
    if (error?.response?.status === 404) return IDENTITY_OFF;
    return IDENTITY_UNKNOWN;
  }
}

// Recusas do login que a tela trata como destino, não como mensagem.
export const LOGIN_DENIED = 'not_credentialed';

/**
 * Motivo de uma recusa no login, para a tela escolher o texto e a saída.
 *
 * O código (`detail.code`) vence. Sem ele, o status decide pela tabela do
 * `/login/face`: 404 rosto, 422 câmera, 503 serviço. Um 403 sem código é
 * ambíguo (credenciamento ou estação) e cai em "serviço", que oferece o
 * PIN — a saída menos errada enquanto o backend não diz qual dos dois.
 */
export function loginFailureCode(error, { viaPin = false } = {}) {
  const response = error && error.response;
  if (!response) return 'recognizer_unavailable';
  const detail = response.data && response.data.detail;
  if (detail && typeof detail === 'object' && detail.code) return detail.code;
  switch (response.status) {
    case 404:
      return 'not_recognized';
    case 413:
    case 422:
      return 'capture_failed';
    case 401:
      // No rosto, 401 é identidade não reconhecida ou sem cadastro — os dois
      // colapsam de propósito, para não enumerar quem tem cadastro.
      return viaPin ? 'pin_refused' : 'not_recognized';
    case 429:
      // Um 429 SEM `detail.code` e' sempre o limitador de requisicoes.
      //
      // O bloqueio de PIN de verdade nunca chega assim: o backend mapeia
      // `PinLockedError` para 429 **com** `code: pin_locked`, e o ramo do
      // codigo resolve antes deste `switch`. Entao a unica coisa que sobra
      // aqui e' o limitador -- em `/login/face` (10/min) e em `/login/pin`
      // (5/min).
      //
      // A primeira versao desta correcao abria excecao para `viaPin`,
      // supondo que ali os dois "coincidem na pratica". Nao coincidem: sao
      // distinguiveis pela presenca do codigo, e a excecao teria mantido a
      // mentira metade do tempo.
      return 'too_many_requests';
    default:
      return 'recognizer_unavailable';
  }
}

/** Dados que o backend manda junto com a recusa (nome, unidade, …). */
export function refusalDetail(error) {
  const detail = error && error.response && error.response.data && error.response.data.detail;
  return detail && typeof detail === 'object' ? detail : {};
}

/**
 * Motivo de uma recusa, para a tela escolher o texto.
 *
 * O backend manda `detail: { code, message }` nas recusas de identidade; o
 * status fica como segunda via enquanto algum caminho ainda responder só com
 * texto.
 */
export function identityErrorCode(error) {
  const response = error && error.response;
  if (!response) return 'network';
  const detail = response.data && response.data.detail;
  if (detail && typeof detail === 'object' && detail.code) return detail.code;
  switch (response.status) {
    case 401:
      return 'pin_refused';
    case 409:
      return 'session_gone';
    case 429:
      // Mesmo raciocinio do `faceErrorCode`: sem `detail.code`, um 429 e'
      // limite de requisicoes. O bloqueio de PIN chega com codigo.
      return 'too_many_requests';
    default:
      return 'unknown';
  }
}

/** Tentativas que restam, quando o backend informa (só no reverify). */
export function remainingAttempts(error) {
  const detail = error && error.response && error.response.data && error.response.data.detail;
  if (detail && typeof detail === 'object' && Number.isInteger(detail.remaining_attempts)) {
    return detail.remaining_attempts;
  }
  return null;
}
